import { apiService } from '@shared/services/apiServices';
import { AxiosError } from 'axios';
import { FACILITY_VIEW_ERROR_MESSAGES } from '../errors';
import { FACILITY_LABELS } from '../constants/facilityLabels';
import facilityEndpoints from './facilityEndpoints';

const DEFAULT_ERROR = 'An error occurred while updating the facility status.';

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return DEFAULT_ERROR;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData === 'object') {
    // Backend shape: { error: { message: string, statusCode: number } }
    const apiError = errorData as {
      message?: string;
      error?: { message?: string } | string;
    };
    if (typeof apiError.error === 'object' && apiError.error?.message) {
      return apiError.error.message;
    }
    if (typeof apiError.error === 'string') {
      return apiError.error;
    }
    if (apiError.message) {
      return apiError.message;
    }
  }
  return DEFAULT_ERROR;
};

export interface UpdateFacilityStatusResult {
  success: boolean;
  error?: string;
}

export const updateFacilityStatusApiService = {
  updateFacilityStatus: async (params: {
    facilityId: string;
    isActive: boolean;
  }): Promise<UpdateFacilityStatusResult> => {
    try {
      const result = await apiService.patchAPI(
        facilityEndpoints.FACILITY_STATUS(params.facilityId),
        { isActive: params.isActive }
      );

      if (!result.error || result.status === 204) {
        return { success: true };
      }

      const statusCode = result.status;
      switch (statusCode) {
        case 400:
          return { success: false, error: 'Please provide valid input.' };
        case 401:
          return {
            success: false,
            error: 'Your session has expired. Please log in again.',
          };
        case 403:
          return {
            success: false,
            error: FACILITY_VIEW_ERROR_MESSAGES.ACCESS_DENIED,
          };
        case 404:
          return { success: false, error: 'Facility not found.' };
        case 409:
          return {
            success: false,
            error: params.isActive
              ? FACILITY_LABELS.ALREADY_ACTIVE
              : FACILITY_LABELS.ALREADY_INACTIVE,
          };
        default:
          if (result.isNetworkError) {
            return {
              success: false,
              error: 'Connection error. Please check your internet connection.',
            };
          }
          return { success: false, error: extractErrorMessage(result.data) };
      }
    } catch (error) {
      if (!(error instanceof AxiosError)) {
        return {
          success: false,
          error: error instanceof Error ? error.message : DEFAULT_ERROR,
        };
      }
      const statusCode = error.response?.status;
      switch (statusCode) {
        case 400:
          return { success: false, error: 'Please provide valid input.' };
        case 401:
          return {
            success: false,
            error: 'Your session has expired. Please log in again.',
          };
        case 403:
          return {
            success: false,
            error: FACILITY_VIEW_ERROR_MESSAGES.ACCESS_DENIED,
          };
        case 404:
          return { success: false, error: 'Facility not found.' };
        case 409:
          return {
            success: false,
            error: params.isActive
              ? FACILITY_LABELS.ALREADY_ACTIVE
              : FACILITY_LABELS.ALREADY_INACTIVE,
          };
        default:
          if (statusCode === undefined && error.request) {
            return {
              success: false,
              error: 'Connection error. Please check your internet connection.',
            };
          }
          return {
            success: false,
            error: extractErrorMessage(error.response?.data),
          };
      }
    }
  },
};
